import React from 'react';
import moment from 'moment-timezone';
import Modal from 'react-modal';
import { upperFirst } from 'lodash';

import GET_COACHING_SESSIONS_QUERY from 'coaching/queries/GetCoachingSessions.gql';

import BoxBody from 'components/BoxBody';
import ConfirmDialog from 'components/ConfirmDialog';
import CoachingConstants from 'coaching/CoachingConstants';
import { deleteCoachingSession } from 'coaching/actions/CoachingActions';
import Divider from 'components/Divider';
import ErrorReporter from 'app/components/ErrorReporter';
import EventTracking from 'lib/EventTracking';
import FormElement from 'components/FormElement';
import Icon from 'components/Icon';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Select from 'components/Select';
import Text from 'components/Text';
import {
  CoachingSessionGQL,
  CoachingInstanceGQL,
} from 'coaching/containers/GetCoachingSessionsAndRecordingsContainer';
import { CoachingUserType } from 'globalTypes';

export type Props = {
  session: CoachingSessionGQL;
  participantUuid: string;
  userName: string;
  userType: CoachingUserType;
  isPastEvent: boolean;
  cancellationReasons: string[];
};

type State = {
  pageNumber: number;
  confirmDeleteDialog?: {
    ongoingSeries: boolean;
    coachingSessionInstanceUuid?: string;
    instanceStartDateTime?: string;
    instanceDateString?: string;
    cancellationReason?: string;
  };
};

export default class CoachingSessionRow extends React.Component<Props, State> {
  state: State = {
    pageNumber: 1,
    confirmDeleteDialog: undefined,
  };

  trackEvent(event: string) {
    EventTracking.trackKey(`coaching.coordinator_tool.participant_sidebar.${event}`);
  }

  formatDate = (dateString: string) => {
    return moment(dateString).format('ddd, MMM D');
  };

  wrapWithDeleteAction = (
    element: React.ReactElement,
    hasMultipleInstances: boolean,
    isInPast: boolean,
    isRowHeader: boolean,
    instance?: CoachingInstanceGQL
  ) => {
    // We shouldn't show the session-level delete button if it's in the past and there are multiple instances
    const shouldHideDeleteButton = isInPast && hasMultipleInstances;

    const handleDeleteAction = () => {
      if (instance) {
        this.trackEvent('remove_session_instance.click');
      } else {
        this.trackEvent('remove_session.click');
      }

      this.setState(
        {
          confirmDeleteDialog: {
            ongoingSeries: hasMultipleInstances,
            instanceDateString: instance?.startDateTime
              ? this.formatDate(instance.startDateTime)
              : undefined,
            instanceStartDateTime: instance?.startDateTime,
            coachingSessionInstanceUuid: instance?.eventId,
          },
        },
        () => EventTracking.trackKey('coaching.coordinator_tool.remove_session_confirm_dialog.view')
      );
    };

    return shouldHideDeleteButton ? (
      <React.Fragment>{element}</React.Fragment>
    ) : (
      // eslint-disable-next-line react/jsx-filename-extension
      <Link
        data-test={`delete-action${isRowHeader ? '-rowHeader' : ''}`}
        color="chalkboard"
        noHover
        onClick={handleDeleteAction}>
        <MediaObject figure="right" center>
          {element}
          <Icon className="CoachingSessionRow-delete" name="x" size="small" />
        </MediaObject>
      </Link>
    );
  };

  renderConfirmDeleteDialog = () => {
    const { confirmDeleteDialog } = this.state;
    if (!confirmDeleteDialog) {
      return null;
    }

    const {
      cancellationReason,
      coachingSessionInstanceUuid,
      instanceDateString,
      instanceStartDateTime,
      ongoingSeries,
    } = confirmDeleteDialog;

    const textConstants = ongoingSeries
      ? CoachingConstants.DELETE_COACHING_SESSION_MODAL_ONGOING
      : CoachingConstants.DELETE_COACHING_SESSION_MODAL_SINGLE;

    return (
      <ConfirmDialog
        data-test="confirm-delete-dialog"
        actionButtonColor="apple"
        cancelButtonColor="apple"
        actionButtonText={textConstants.confirmText}
        confirmEventKey="coaching.coordinator_tool.remove_session_confirm_dialog.remove_session.click"
        cancelButtonText={textConstants.cancelText}
        cancelEventKey="coaching.coordinator_tool.remove_session_confirm_dialog.keep_session.click"
        preConfirmPromise={() =>
          deleteCoachingSession(
            this.props.session.uuid,
            cancellationReason,
            coachingSessionInstanceUuid,
            instanceStartDateTime,
            [
              {
                query: GET_COACHING_SESSIONS_QUERY,
                variables: { userUuid: this.props.participantUuid, userType: this.props.userType },
              },
            ]
          )
        }
        onRequestHide={() => this.setState({ confirmDeleteDialog: undefined })}
        title={<Text font="serif">{textConstants.headerText}</Text>}>
        <Text align="left">
          <Row size="xxsmall">
            <Text weight="semibold">{this.props.userName}</Text>
          </Row>
          {instanceDateString ?? null}
          {this.renderHeader()}
          {this.renderMetadata()}
          <Divider size="medium" />
          {this.renderCancellationReasons()}
        </Text>
      </ConfirmDialog>
    );
  };

  renderHeader = () => {
    const { session } = this.props;
    const headerText =
      session.instances.length > 1 ? 'Ongoing' : this.formatDate(session.startDateTime);
    return (
      <Row size="xxsmall">
        <Text truncate size="small">
          <MediaObject center gutter="small">
            <Text>{headerText}</Text>
            <Text color="graphite">{session.coach.name}</Text>
          </MediaObject>
        </Text>
      </Row>
    );
  };

  renderMetadata = () => {
    const { session } = this.props;
    const startTime = moment(session.startDateTime).format('h:mm A');
    return <Text size="small">{`${startTime} • ${session.duration} min `}</Text>;
  };

  renderCancellationReasons = () => {
    const { confirmDeleteDialog } = this.state;
    if (!confirmDeleteDialog) {
      return (
        <ErrorReporter error="CoachingSessionRow - tried to render cancellation reasons outside of the deletion dialog" />
      );
    }
    return (
      <Row>
        <FormElement
          data-test="cancellation-reason-dropdown"
          label="Select a reason"
          element={Select}
          elementProps={{
            options: this.props.cancellationReasons.map(reason => ({
              label: upperFirst(reason.replace(/_/g, ' ')),
              value: reason,
            })),
            value: confirmDeleteDialog.cancellationReason,
            name: 'reason',
          }}
          onValidChange={value =>
            this.setState({
              confirmDeleteDialog: {
                ...confirmDeleteDialog,
                cancellationReason: value,
              },
            })
          }
        />
      </Row>
    );
  };

  renderInstances = () => {
    const {
      session: { instances },
    } = this.props;
    if (instances.length < 2) {
      return null;
    }

    const numberOfInstancesToShow =
      this.state.pageNumber * CoachingConstants.COACHING_SESSIONS_PAGE_LENGTH;
    const instancesToShow = instances.slice(0, numberOfInstancesToShow);
    return (
      <Row size="xsmall">
        <BoxBody size="xxsmall">
          {instancesToShow.map(instance => {
            const dateString = this.formatDate(instance.startDateTime);
            return (
              <div
                className="CoachingSessionRow-instance"
                key={instance.eventId}
                data-test="coaching-session-instance">
                {this.wrapWithDeleteAction(
                  <Text element="div">{dateString}</Text>,
                  false,
                  this.props.isPastEvent,
                  false,
                  instance
                )}
              </div>
            );
          })}
          {this.state.pageNumber === 1 &&
            instances.length > CoachingConstants.COACHING_SESSIONS_PAGE_LENGTH && (
              <Text className="CoachingSessionRow-showMore" element="div" weight="semibold">
                <Link
                  onClick={() => {
                    this.trackEvent('session_show_more_instances.click');
                    this.setState({ pageNumber: 2 });
                  }}>
                  Show more
                </Link>
              </Text>
            )}
        </BoxBody>
      </Row>
    );
  };

  sessionLevelDeletableElement = () => {
    // If the session is in the past and there's only one instance left, we want to delete that instance
    // rather than the whole series
    const instanceToDelete =
      this.props.isPastEvent && this.props.session.instances.length == 1
        ? this.props.session.instances[0]
        : undefined;
    return this.wrapWithDeleteAction(
      <div>
        {this.renderHeader()}
        {this.renderMetadata()}
      </div>,
      this.props.session.instances.length > 1,
      this.props.isPastEvent,
      true,
      instanceToDelete
    );
  };

  render() {
    return (
      <React.Fragment>
        <Modal data-test="confirm-delete-modal" isOpen={!!this.state.confirmDeleteDialog}>
          {this.renderConfirmDeleteDialog()}
        </Modal>
        <div className="CoachingSessionRow">
          <BoxBody size="xxsmall" className="CoachingSessionRow-header">
            {this.sessionLevelDeletableElement()}
          </BoxBody>
          {this.renderInstances()}
        </div>
      </React.Fragment>
    );
  }
}
